// b15 (BK39): guiden "Kom igång" mellan appen och molnet (paket/moln/src/guiden.ts): fynden,
// frågorna med svaren, svaren som appen skickar och förslaget "Så här blir det".

import type { Formaga, Fyndkalla, Larmkalla, Produkt, Status } from "@koll/katalog";
import type { Homeyinstallningar } from "./homey.ts";
import type { Forseglad } from "./lada.ts";
import type { Platstyp, Roll } from "./typer.ts";
import type { Uppstallning } from "./uppstallning.ts";
import type { Nyckellage, Victroninstallningar } from "./victron.ts";

/**
 * b17: delarna av "Det här hittade vi", i ordning:
 *   - anvands: det som Båtkoll (Stugkoll) använder: status stöds eller beta, eller en enhet vars
 *     entiteter redan är kopplade till signaler. Ibockat från början;
 *   - ovrigt_ha och ovrigt_homey: övrigt i Home Assistant och Homey, hopfällt och inte ibockat;
 *   - ovrigt: annat som katalogen känner men som inte används än (planerat);
 *   - okand: okända enheter, aldrig ibockade från början;
 *   - annan (b18): det som ägaren har markerat som "Finns på en annan plats", sist och hopfällt.
 */
export const GUIDEGRUPPER = [
  "anvands",
  "ovrigt_ha",
  "ovrigt_homey",
  "ovrigt",
  "okand",
  "annan",
] as const;

/** b18: ägarens val för ett fynd: med, hoppa (över), eller annan (finns på en annan plats). */
export type Fyndval = "med" | "hoppa" | "annan";

export type Guidegrupp = (typeof GUIDEGRUPPER)[number];

export interface Guidefynd {
  id: string;
  kalla: Fyndkalla;
  namn: string;
  tillverkare: string | null;
  /** Katalogens status, eller null för en okänd enhet. */
  status: Status | null;
  /** Statusen i ord: "stöds", "beta", "planerad" eller "okänd". */
  statustext: string;
  system: string | null;
  /** Enheten i katalogen, eller null. */
  enhet: string | null;
  kand: boolean;
  /** Ägarens val: med, hoppa (över), annan (b18), eller null (inte valt än). */
  val: Fyndval | null;
  /** Ägarens namn för enheten, eller null. */
  egetNamn: string | null;
  senast: number;
  /**
   * b16: nyckeln för en Victron-enhet över Bluetooth: saknas, fel, finns, eller skickad (på väg
   * till navet). Null för andra enheter.
   */
  nyckel?: Nyckellage | "skickad" | null;
  /** b17: delen i "Det här hittade vi". */
  grupp?: Guidegrupp;
  /** b17: förvalet när ägaren inte har valt: med för det som används, annars hoppa (över). */
  forval?: "med" | "hoppa";
  /** b17: raden som enheten hör till: samma modell från samma källa och integration. */
  rad?: string;
  /** b17: förmågorna som navet läser ur enheten. */
  formagor?: Formaga[];
  /** b17: enheten har entiteter som navet redan har kopplat (i navets inställningar). */
  kopplad?: boolean;
  /**
   * b18 (PL7): enhetens namn i Home Assistant eller Homey, rummet och modellen för navet eller
   * basstationen den sitter på, så att ägaren känner igen den. Bara för platsens ägare och läsare.
   */
  enhetsnamn?: string | null;
  rum?: string | null;
  via?: string | null;
}

export interface Guidefraga {
  id: string;
  nr: number;
  sort: string;
  text: string;
  hjalp: string | null;
  alternativ: { id: string; text: string }[];
  /** Svaret på platsen nu, eller förvalet. */
  svar: unknown;
}

export interface Guidelage {
  produkt: Produkt;
  produktnamn: string;
  typ: Platstyp;
  katalog: number;
  fynd: Guidefynd[];
  /** "Det här hittade vi" i en mening, till exempel "2 motorer (Volvo Penta), 4 Ruuvi". */
  sammanfattning: string;
  /** En båt där navet inte hör någon motor: be ägaren slå på tändningen. */
  ingaMotorer: string | null;
  fragor: Guidefraga[];
  delaOkanda: boolean;
  /** När guiden kördes klart senast, eller null. */
  klar: number | null;
  /** b16: Victron: GX-enhetens adress, förslaget och navets nyckel för lådan. Null utan nav. */
  victron?: Victroninstallningar | null;
  /** b16: de tysta tiderna för kontot som kör guiden (fråga 6), hela timmar 0–23. */
  tysta?: { fran: number; till: number };
  /** b17: Homey: adressen, förslaget och navets nyckel för lådan. Null utan nav. */
  homey?: Homeyinstallningar | null;
  /** b18: ägarens val under Ändra förra gången guiden kördes. */
  larmkalla?: string | null;
  effekt?: string[] | null;
}

export interface Guidesvar {
  typ?: Platstyp;
  fynd?: { id: string; val: Fyndval; namn?: string | null }[];
  mall?: string;
  omraden?: string[];
  drift?: "i_drift" | "ej_i_drift";
  larm?: string[];
  konton?: { epost: string; roll: Roll }[];
  badtemperatur?: boolean;
  delaOkanda?: boolean;
  /** b16: GX-enhetens adress (null tar bort den), och VRM Portal ID om navet inte hittar det. */
  gx?: { adress: string | null; portal?: string | null };
  /** b16: nycklarna för Victron-enheter över Bluetooth, förseglade till navet i webbläsaren. */
  victronNycklar?: { fynd: string; lada: Forseglad }[];
  /** b16: de tysta tiderna för kontot som kör guiden (fråga 6). */
  tysta?: { fran: number; till: number };
  /** b17: Homeys adress (null tar bort den, och navet slutar läsa Homey). */
  homey?: { adress: string | null };
  /** b17: Homeys nyckel, förseglad till navet i webbläsaren. */
  homeyNyckel?: Forseglad;
  /** b17: GX-enhetens användarnamn och lösenord, förseglade till navet i webbläsaren. */
  gxInloggning?: Forseglad;
  /** b18: fyndet som larmets läge kommer från, valt under Ändra. */
  larmkalla?: string;
  /** b18: fynden vars effekt och energi kopplas, valda under Ändra. */
  effekt?: string[];
}

export interface Guidegrans {
  grans: string;
  text: string;
  varde: number;
  forval: number;
  /** Platsen har ett eget värde, som guiden inte ändrar. */
  eget: boolean;
}

export interface Guideforslag {
  produktnamn: string;
  mall: { id: string; namn: string; text: string } | null;
  kort: string[];
  signaler: string[];
  /** Båtens uppställning efter bekräftelsen, med grupperna i ord. */
  uppstallning: { text: string; grupper: string[]; uppstallning: Uppstallning } | null;
  granser: Guidegrans[];
  larm: string[];
  /** Exakt det som ändras när ägaren bekräftar. Tom: ingenting ändras. */
  andringar: { falt: string; text: string }[];
  /** Förslagets nyckel, som bekräftelsen skickar tillbaka. */
  nyckel: string;
  /** b18: enheterna som larmets läge kan komma från, med den valda (under Ändra). */
  larmkallor?: {
    fynd: string;
    namn: string;
    kalla: Larmkalla | null;
    vald: boolean;
    /** b20: navet läser redan larmet i sina egna inställningar. */
    iNavet?: boolean;
  }[];
  /** b18: de valda enheterna med effekten eller energin, och de som kopplas (under Ändra). */
  effekt?: { fynd: string; namn: string; elmatare: boolean; vald: boolean }[];
}
